import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import type { IState } from 'legacy/lut/store'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import {
  getAttentionSeverityLevels,
  getAttentionStatusRole,
  type TAttentionStatusSeverity,
} from 'partner360/entities/attentionStatuses'

import { getAttentionGeoLevelId, UNASSIGNED_MANAGER_KEY, UNASSIGNED_PBM_KEY } from '../../../lib'
import {
  closeCockpitAttentionPartner,
  enterCockpitAttentionGroup,
  goToCockpitAttentionLevel,
  openCockpitAttentionPartner,
  setCockpitSelectedAttentionStatus,
  switchCockpitAttentionPartnerStatus,
} from '../../../model/financialCockpitSlice'
import {
  selectCockpitAttentionDetailPartner,
  selectCockpitAttentionLevel,
  selectCockpitAttentionOwnersByPartner,
  selectCockpitAttentionSearch,
  selectCockpitAttentionSelectedStatus,
  selectCockpitCountsPlansByPartner,
  selectCockpitMultiPartnerAttentionStatuses,
  selectCockpitPartnerAttentionStatuses,
  selectCockpitUserAccess,
} from '../../../model/selectors'
import type { TCockpitAttentionGrouping } from '../../../model/types'
import { countAttentionPbms, mergeSameTitledStatuses, sortAttentionItemsBySeverity } from '../lib'
import type {
  IAttentionStatusesCrumb,
  IAttentionStatusesDetail,
  IAttentionStatusesItem,
  IAttentionStatusesTile,
  IAttentionStatusesUnit,
} from '../types'

import { useAttentionDrillMode } from './useAttentionDrillMode'
import {
  getAttentionGeoUnit,
  MANAGER_UNIT,
  PARTNER_ATTENTION_UNIT,
  PARTNER_UNIT,
  PBM_UNIT,
  STATUS_UNIT,
  useAttentionStatusesText,
} from './useAttentionStatusesText'

const UNASSIGNED_KEYS = new Set([UNASSIGNED_PBM_KEY, UNASSIGNED_MANAGER_KEY])

/** What a grouped level counts: a place at the depth the tree stands on, a PBM or a manager. */
export const getAttentionGroupUnit = (
  grouping: TCockpitAttentionGrouping,
  depth: number
): IAttentionStatusesUnit => {
  switch (grouping) {
    case 'pbm':
      return PBM_UNIT
    case 'manager':
      return MANAGER_UNIT
    case 'market':
      return getAttentionGeoUnit(getAttentionGeoLevelId(depth))
    default:
      return PARTNER_UNIT
  }
}

/**
 * The manager cockpit's tile. It shows the statuses raised across the partners currently in view,
 * then those partners grouped level by level — by geo, PBM, manager, or not at all — and finally
 * the partners themselves. Every level is grouped on its own, from the header's switcher; see
 * `resolveAttentionLevel`.
 *
 * Partner rows carry the way to the partner's own plan. A PBM works their own partners, so for them
 * a partner row also opens the status' detail for that partner, with the actions the partner's own
 * cockpit offers; for every other role it opens nothing inside the tile. Rows of a PBM or a manager
 * carry a reminder to that person.
 */

/** A partner's status row: `Overall` lists the same status once per offering that raised it. */
const toPartnerStatusId = (bitValue: number, offering: string | undefined) =>
  `${bitValue}|${offering ?? ''}`
export const useManagerAttentionStatuses = (): IAttentionStatusesTile => {
  const dispatch = useAppDispatch()
  const text = useAttentionStatusesText()

  useAttentionDrillMode('multiple-partner')

  const raisedStatuses = useSelector(selectCockpitMultiPartnerAttentionStatuses)
  const selectedStatus = useSelector(selectCockpitAttentionSelectedStatus)
  const countsPlansByPartner = useSelector(selectCockpitCountsPlansByPartner)
  /* A distributor plans all its offerings as one, so its view titles by partners what counts plans. */
  const statuses = useMemo(
    () => (countsPlansByPartner ? raisedStatuses.map(text.countedByPartner) : raisedStatuses),
    [countsPlansByPartner, raisedStatuses, text]
  )
  const status = useMemo(
    () =>
      countsPlansByPartner && selectedStatus
        ? text.countedByPartner(selectedStatus)
        : selectedStatus,
    [countsPlansByPartner, selectedStatus, text]
  )
  const level = useSelector(selectCockpitAttentionLevel)
  const ownersByPartner = useSelector(selectCockpitAttentionOwnersByPartner)
  const search = useSelector(selectCockpitAttentionSearch)
  const query = search.trim().toLowerCase()
  const { role } = useSelector(selectCockpitUserAccess)
  const statusRole = getAttentionStatusRole(role)
  const isPbm = statusRole === 'pbm'
  const detailPartner = useSelector(selectCockpitAttentionDetailPartner)
  const detailPartnerRaised = useSelector((state: IState) =>
    selectCockpitPartnerAttentionStatuses(state, detailPartner?.partnerId ?? '')
  )
  const detailPartnerStatuses = useMemo(
    () =>
      mergeSameTitledStatuses(
        detailPartnerRaised,
        (status) => text.statusTitleParts(status, 1).text
      ),
    [detailPartnerRaised, text]
  )

  const statusItems = useMemo<IAttentionStatusesItem[]>(
    () =>
      sortAttentionItemsBySeverity(
        statuses.map((aggregated) => {
          /* The count leads the catalog's title — `X of Y partners …` — and the row sets it in its
             own weight, so it travels beside the sentence rather than inside it. A `… plans …`
             title counts plans, not the partners behind them. */
          const { count, text: title } = text.statusTitleParts(aggregated, aggregated.raisedCount)

          return {
            id: String(aggregated.bitValue),
            bitValue: aggregated.bitValue,
            title,
            titleCount: count,
            shareBase: aggregated.raisedScope || undefined,
            severity: aggregated.severity,
            raiseCount: aggregated.raisedCount,
            actionLabel: text.phrase.actionLabel(aggregated.bitValue, statusRole),
            opensDetail: false,
          }
        })
      ),
    [statuses, statusRole, text]
  )

  /* A partner raising several statuses is one partner: the split under the robot counts it once,
     at the worst level it raised, so the levels add up to the partners that need attention. */
  const severityCounts = useMemo(() => {
    const worstByPartner = new Map<string, TAttentionStatusSeverity>()

    statuses.forEach(({ partnerIds, severity }) =>
      partnerIds.forEach((partnerId) =>
        worstByPartner.set(partnerId, Math.max(worstByPartner.get(partnerId) ?? 0, severity))
      )
    )

    const worst = [...worstByPartner.values()]

    return getAttentionSeverityLevels(worst.map((severity) => ({ severity }))).map((severity) => ({
      severity,
      count: worst.filter((it) => it === severity).length,
    }))
  }, [statuses])

  /** How many statuses each partner raised, so a partner row counts what that partner owes. */
  const statusCountByPartner = useMemo(() => {
    const counts = new Map<string, number>()

    statuses.forEach(({ partnerIds }) =>
      partnerIds.forEach((partnerId) => counts.set(partnerId, (counts.get(partnerId) ?? 0) + 1))
    )

    return counts
  }, [statuses])

  const groupItems = useMemo<IAttentionStatusesItem[]>(() => {
    if (!status || !level) {
      return []
    }

    const isOwnerLevel = level.grouping === 'pbm' || level.grouping === 'manager'
    /* A PBM row is one PBM, so only the other groupings say how many stand behind the row. */
    const countsPbms = level.grouping !== 'pbm'

    return (
      level.nodes
        /* An owner is searched for by either of the two things the payload names them by. */
        .filter(
          ({ name, itemId }) =>
            !query || name.toLowerCase().includes(query) || itemId.toLowerCase().includes(query)
        )
        .map((node) => ({
          id: node.itemId,
          title: node.name,
          severity: status.severity,
          actionLabel: text.phrase.openLevel(getAttentionGroupUnit(level.grouping, node.depth)),
          meta: [
            { count: node.partnerIds.length, unit: PARTNER_UNIT },
            ...(countsPbms
              ? [{ count: countAttentionPbms(node.partnerIds, ownersByPartner), unit: PBM_UNIT }]
              : []),
          ],
          opensDetail: false,
          opensLevel: true,
          reminder:
            isOwnerLevel && !UNASSIGNED_KEYS.has(node.itemId)
              ? { email: node.itemId, name: node.name }
              : undefined,
          reminderRoute: {
            bitValue: status.bitValue,
            actionId: level.grouping === 'manager' ? 'remindManager' : 'remindPbm',
          },
          reminderList: { partners: node.partnerIds.map((partnerId) => ({ partnerId })) },
        }))
    )
  }, [level, ownersByPartner, query, status, text])

  const partnerItems = useMemo<IAttentionStatusesItem[]>(() => {
    if (!status || !level) {
      return []
    }

    return level.partners
      .filter(({ partnerName }) => !query || partnerName.toLowerCase().includes(query))
      .map((partner) => ({
        id: partner.partnerId,
        title: partner.partnerName,
        severity: status.severity,
        actionLabel: text.phrase.actionLabel(status.bitValue, statusRole),
        meta: [
          ...(partner.country ? [partner.country] : []),
          { count: statusCountByPartner.get(partner.partnerId) ?? 0, unit: STATUS_UNIT },
          { count: countAttentionPbms([partner.partnerId], ownersByPartner), unit: PBM_UNIT },
        ],
        opensDetail: isPbm,
        partnerId: partner.partnerId,
        pbms: (ownersByPartner.get(partner.partnerId)?.pbms ?? []).map(
          ({ ownerId, ownerName }) => ({
            email: ownerId,
            name: ownerName,
            partnerId: partner.partnerId,
          })
        ),
        reminderRoute: { bitValue: status.bitValue, actionId: 'remindPbm' },
      }))
  }, [isPbm, level, ownersByPartner, query, status, statusCountByPartner, statusRole, text])

  const selectItem = useCallback(
    (id: string) => {
      if (!status || !level) {
        dispatch(setCockpitSelectedAttentionStatus({ bitValue: Number(id) }))
        return
      }

      if (level.nodes.some(({ itemId }) => itemId === id)) {
        dispatch(enterCockpitAttentionGroup({ grouping: level.grouping, itemId: id }))
        return
      }

      if (isPbm && level.partners.some(({ partnerId }) => partnerId === id)) {
        dispatch(openCockpitAttentionPartner({ partnerId: id, offering: null }))
      }
    },
    [dispatch, isPbm, level, status]
  )

  /** `-1` leaves the status behind; any other index keeps that many steps of the path. Either way
   *  an open partner closes: its detail is the leaf below the level. */
  const goToLevel = useCallback(
    (index: number) => {
      dispatch(closeCockpitAttentionPartner())
      dispatch(
        index < 0 ? setCockpitSelectedAttentionStatus(null) : goToCockpitAttentionLevel(index)
      )
    },
    [dispatch]
  )

  const selectRelatedStatus = useCallback(
    (id: string) => {
      const [bitValue, offering] = id.split('|')

      dispatch(
        switchCockpitAttentionPartnerStatus({
          bitValue: Number(bitValue),
          offering: offering || null,
        })
      )
    },
    [dispatch]
  )

  /* The open partner's status, as that partner's own cockpit states it — per offering, with the
     offering it was opened for where one was — and the rest of what the partner holds open. */
  const detail = useMemo<IAttentionStatusesDetail | null>(() => {
    if (!isPbm || !status || !detailPartner) {
      return null
    }

    const partner = level?.partners.find(({ partnerId }) => partnerId === detailPartner.partnerId)
    const raised =
      detailPartnerStatuses.find(
        ({ bitValue, offerings }) =>
          bitValue === status.bitValue &&
          (!detailPartner.offering || offerings[0] === detailPartner.offering)
      ) ?? detailPartnerStatuses.find(({ bitValue }) => bitValue === status.bitValue)

    if (!partner || !raised) {
      return null
    }

    return {
      status: { ...raised, title: text.statusTitleParts(raised, 1).text },
      partnerId: partner.partnerId,
      partnerName: partner.partnerName,
      relatedStatuses: sortAttentionItemsBySeverity(
        detailPartnerStatuses
          .filter((other) => other !== raised)
          .map((other) => ({
            id: toPartnerStatusId(other.bitValue, other.offerings[0]),
            title: text.statusTitleParts(other, 1).text,
            severity: other.severity,
            actionLabel: text.phrase.actionLabel(other.bitValue, statusRole),
            opensDetail: true,
          }))
      ),
    }
  }, [detailPartner, detailPartnerStatuses, isPbm, level, status, statusRole, text])

  if (!status || !level) {
    return {
      items: statusItems,
      unit: PARTNER_ATTENTION_UNIT,
      severityCounts,
      crumbs: [],
      detail: null,
      selectItem,
      goToLevel,
      selectRelatedStatus,
    }
  }

  const crumbs: IAttentionStatusesCrumb[] = [
    {
      id: String(status.bitValue),
      label: text.statusTitle(status, status.raisedCount, status.raisedScope),
    },
    ...level.steps.map(({ node }) => ({ id: node.itemId, label: node.name })),
  ]
  const isGroupLevel = level.nodes.length > 0

  return {
    items: isGroupLevel ? groupItems : partnerItems,
    totalCount: isGroupLevel ? groupItems.length : partnerItems.length,
    unit: isGroupLevel ? getAttentionGroupUnit(level.grouping, level.nodes[0].depth) : PARTNER_UNIT,
    levelSeverity: status.severity,
    crumbs,
    detail,
    selectItem,
    goToLevel,
    selectRelatedStatus,
  }
}
